import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { fetchModels, fetchStats } from '../api';
import {
  CallSliceTable,
  ExecutionDurationPanel,
  StartupBreakdownPanel,
} from '../components/PerformancePanels';
import { RetriesPanel } from '../components/RetriesPanel';
import { buildLatencySeries, formatSeconds } from '../components/latency';
import type { ApiEndpointModel, ApiStatsResponse } from '../types';

type RangeKey = '24h' | '7d' | '30d';
type IntervalKey = '5m' | '15m' | '1h' | '6h' | '1d';

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': '7 days',
  '30d': '30 days',
};

const RANGE_MS: Record<RangeKey, number> = {
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
  '30d': 30 * 24 * 60 * 60_000,
};

const INTERVAL_LABELS: Record<IntervalKey, string> = {
  '5m': '5 min',
  '15m': '15 min',
  '1h': '1 hour',
  '6h': '6 hours',
  '1d': '1 day',
};

const INTERVAL_MS: Record<IntervalKey, number> = {
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '1d': 24 * 60 * 60_000,
};

const DEFAULT_INTERVAL: Record<RangeKey, IntervalKey> = {
  '24h': '5m',
  '7d': '6h',
  '30d': '1d',
};

const COLOR_SUCCESS = '#70B77E';
const COLOR_ERROR = '#F0544F';

// Slice colours for the cost split. Deliberately avoids the success/error
// greens and reds above, which carry meaning elsewhere on this page.
const COST_COLORS = [
  '#6366f1',
  '#0ea5e9',
  '#8b5cf6',
  '#14b8a6',
  '#f59e0b',
  '#ec4899',
  '#64748b',
];

function formatUsd(value: number): string {
  if (value <= 0) return '$0';
  if (value < 0.01) return `$${value.toFixed(5)}`;
  return `$${value.toFixed(4)}`;
}

/**
 * Cross-model statistics for the API-wrapper (apigen) section. Aggregation is
 * performed server-side (`GET /v1/stats`) so the response stays small no matter
 * how many calls fall in the selected window.
 */
export function StatisticsPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>('24h');
  const [intervalKey, setIntervalKey] = useState<IntervalKey>('5m');
  const [data, setData] = useState<ApiStatsResponse | null>(null);
  const [models, setModels] = useState<ApiEndpointModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Model labels rarely change; load them once.
  useEffect(() => {
    let cancelled = false;
    fetchModels()
      .then((m) => {
        if (!cancelled) setModels(m);
      })
      .catch(() => {
        /* labels are best-effort; fall back to raw model_type */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const to = Date.now();
    const from = to - RANGE_MS[rangeKey];
    fetchStats({ from, to, bucketSeconds: INTERVAL_MS[intervalKey] / 1000 })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rangeKey, intervalKey, reloadKey]);

  function selectRange(key: RangeKey) {
    setRangeKey(key);
    setIntervalKey(DEFAULT_INTERVAL[key]);
  }

  const labelFor = useMemo(() => {
    const map = new Map(models.map((m) => [m.key, m.label]));
    return (key: string) => map.get(key) || key;
  }, [models]);

  // Route slug for an endpoint, so the error/slow tables can link to its history.
  const slugFor = useMemo(() => {
    const map = new Map(models.map((m) => [m.key, m.path.replace(/^\/v1\//, '')]));
    return (key: string) => map.get(key) || null;
  }, [models]);

  const totals = data?.totals;

  // Merge the sparse server series into the full, evenly-spaced bucket range so
  // empty buckets render as gaps in the chart.
  const buckets = useMemo(() => {
    const step = INTERVAL_MS[intervalKey];
    const end = Date.now();
    const start = Math.floor((end - RANGE_MS[rangeKey]) / step) * step;
    const labelFmt =
      step >= INTERVAL_MS['1d'] ? 'MMM d' : rangeKey === '24h' ? 'HH:mm' : 'M/d HH:mm';

    const map = new Map<number, { success: number; error: number }>();
    for (let t = start; t <= end; t += step) map.set(t, { success: 0, error: 0 });

    for (const p of data?.series || []) {
      if (!p.ts) continue;
      const bucket = Math.floor(new Date(p.ts).getTime() / step) * step;
      const entry = map.get(bucket);
      if (!entry) continue;
      entry.success += p.success;
      entry.error += p.error;
    }

    return Array.from(map.entries()).map(([t, v]) => ({
      label: format(new Date(t), labelFmt),
      success: v.success,
      error: v.error,
    }));
  }, [data, rangeKey, intervalKey]);

  // Latency series share the bucket grid with the counts chart above.
  const bucketGrid = useMemo(() => {
    const step = INTERVAL_MS[intervalKey];
    const labelFmt =
      step >= INTERVAL_MS['1d'] ? 'MMM d' : rangeKey === '24h' ? 'HH:mm' : 'M/d HH:mm';
    return { step, rangeMs: RANGE_MS[rangeKey], labelFmt };
  }, [rangeKey, intervalKey]);

  const startupPoints = useMemo(
    () =>
      buildLatencySeries(
        data?.series,
        'startup',
        bucketGrid.rangeMs,
        bucketGrid.step,
        bucketGrid.labelFmt,
      ),
    [data, bucketGrid],
  );
  const execPoints = useMemo(
    () =>
      buildLatencySeries(
        data?.series,
        'exec',
        bucketGrid.rangeMs,
        bucketGrid.step,
        bucketGrid.labelFmt,
      ),
    [data, bucketGrid],
  );

  const perModel = data?.by_model || [];

  // Cost split: biggest spender first, so slice colours stay stable with rank.
  const costByModel = useMemo(
    () =>
      (data?.by_model || [])
        .filter((m) => m.cost_usd > 0)
        .sort((a, b) => b.cost_usd - a.cost_usd)
        .map((m, i) => ({
          key: m.model_type,
          name: labelFor(m.model_type),
          value: m.cost_usd,
          color: COST_COLORS[i % COST_COLORS.length],
        })),
    [data, labelFor],
  );
  const costTotal = costByModel.reduce((sum, s) => sum + s.value, 0);

  const total = totals?.total ?? 0;
  const errorRate = totals && totals.total ? totals.errors / totals.total : 0;
  const recentErrors = data?.recent_errors || [];
  const slowest = data?.slowest || [];

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Statistics</h1>
          <p className="text-sm text-slate-500 mt-1">
            Aggregate usage across all API endpoints in the selected time frame.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => selectRange(key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  rangeKey === key
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {RANGE_LABELS[key]}
              </button>
            ))}
          </div>
          <select
            value={intervalKey}
            onChange={(e) => setIntervalKey(e.target.value as IntervalKey)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            title="Bucket interval"
          >
            {(Object.keys(INTERVAL_LABELS) as IntervalKey[]).map((key) => (
              <option key={key} value={key}>
                {INTERVAL_LABELS[key]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setReloadKey((k) => k + 1)}
            title="Refresh"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0014-6M19 5a9 9 0 00-14 6" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          Failed to load statistics: {error}
        </div>
      )}

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total calls" value={String(total)} sub="in this period" />
        <StatCard
          label="Errors"
          value={String(totals?.errors ?? 0)}
          sub={`${Math.round((1 - errorRate) * 100)}% success rate`}
          tone={(totals?.errors ?? 0) > 0 ? 'danger' : undefined}
        />
        <StatCard label="Error rate" value={`${(errorRate * 100).toFixed(1)}%`} sub="of all calls" />
        <StatCard label="Total cost" value={formatUsd(totals?.cost_usd ?? 0)} sub="in this period" />
        <StatCard label="Avg cost / call" value={formatUsd(totals?.avg_cost_usd ?? 0)} sub="mean across calls" />
      </div>

      {/* Request counts chart */}
      <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Calls over time</h3>
          <div className="text-right">
            <span className="text-lg font-bold text-slate-700">{total}</span>
            <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
          </div>
        </div>
        <div className="h-64">
          {loading && !data ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Loading statistics…
            </div>
          ) : total > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={24}
                  formatter={(value: string) => <span className="text-slate-600 text-xs">{value}</span>}
                />
                <Bar dataKey="success" name="Success" stackId="a" fill={COLOR_SUCCESS} />
                <Bar dataKey="error" name="Error" stackId="a" fill={COLOR_ERROR} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-slate-400">
              No calls in this period
            </div>
          )}
        </div>
      </div>

      {/* Per-model breakdown, with the cost split beside it */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">By model</h3>
            <div className="text-right">
              <span className="text-lg font-bold text-slate-700">{perModel.length}</span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">models</span>
            </div>
          </div>
          {perModel.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {/* Column header */}
              <div className="hidden sm:grid grid-cols-[1fr_repeat(4,minmax(0,6rem))] gap-2 pb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <span>Model</span>
                <span className="text-right">Calls</span>
                <span className="text-right">Errors</span>
                <span className="text-right">Error rate</span>
                <span className="text-right">Cost</span>
              </div>
              {perModel.map((m) => {
                const rate = m.total ? m.errors / m.total : 0;
                return (
                  <div
                    key={m.model_type}
                    className="grid grid-cols-2 sm:grid-cols-[1fr_repeat(4,minmax(0,6rem))] gap-2 py-2.5 text-sm items-center"
                  >
                    <span className="font-medium text-slate-800 truncate col-span-2 sm:col-span-1">
                      {labelFor(m.model_type)}
                    </span>
                    <span className="text-right tabular-nums text-slate-700" data-label="Calls">
                      <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Calls</span>
                      {m.total}
                    </span>
                    <span
                      className={`text-right tabular-nums ${m.errors > 0 ? 'text-danger-700' : 'text-slate-700'}`}
                      data-label="Errors"
                    >
                      <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Errors</span>
                      {m.errors}
                    </span>
                    <span className="text-right tabular-nums text-slate-700" data-label="Error rate">
                      <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Err rate</span>
                      {(rate * 100).toFixed(1)}%
                    </span>
                    <span className="text-right tabular-nums text-slate-700" data-label="Cost">
                      <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Cost</span>
                      {formatUsd(m.cost_usd)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center text-sm text-slate-400 py-8">
              No calls in this period
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Cost by model</h3>
            <div className="text-right">
              <span className="text-lg font-bold text-slate-700">{formatUsd(costTotal)}</span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
            </div>
          </div>
          {loading && !data ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500 py-8">
              Loading statistics…
            </div>
          ) : costByModel.length > 0 ? (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={costByModel}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="52%"
                      outerRadius="80%"
                      paddingAngle={2}
                      stroke="none"
                    >
                      {costByModel.map((slice) => (
                        <Cell key={slice.key} fill={slice.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        fontSize: '12px',
                      }}
                      formatter={(value: number, name: string) => [
                        `${formatUsd(value)} · ${((value / costTotal) * 100).toFixed(1)}%`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-3 space-y-1.5">
                {costByModel.map((slice) => (
                  <li key={slice.key} className="flex items-center gap-2 text-xs">
                    <span
                      className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ backgroundColor: slice.color }}
                    />
                    <span className="text-slate-600 truncate flex-1" title={slice.name}>
                      {slice.name}
                    </span>
                    <span className="tabular-nums text-slate-500">
                      {((slice.value / costTotal) * 100).toFixed(0)}%
                    </span>
                    <span className="tabular-nums font-medium text-slate-700 w-16 text-right">
                      {formatUsd(slice.value)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 py-8">
              No cost in this period
            </div>
          )}
        </div>
      </div>

      {/* Latency: the wait before a request runs, then the run itself */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StartupBreakdownPanel data={data} points={startupPoints} loading={loading} />
        <ExecutionDurationPanel data={data} points={execPoints} loading={loading} />
      </div>

      {/* Row-level outliers, with the retry split under the errors it explains */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <CallSliceTable
          title="Slower requests"
          rows={slowest}
          loading={loading}
          emptyLabel="No completed calls in this period"
          slugFor={slugFor}
          labelFor={labelFor}
          right={
            <div className="text-right shrink-0">
              <span className="text-lg font-bold text-slate-700 tabular-nums">
                {formatSeconds(totals?.p90 ?? null)}
              </span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">p90</span>
            </div>
          }
        />
        <div className="space-y-4">
          <CallSliceTable
            title="Recent errors"
            rows={recentErrors}
            loading={loading}
            emptyLabel="No errors in this period"
            emptyTone="success"
            slugFor={slugFor}
            labelFor={labelFor}
            right={
              <div className="text-right shrink-0">
                <span className="text-lg font-bold text-danger-700 tabular-nums">
                  {totals?.errors ?? 0}
                </span>
                <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
              </div>
            }
          />
          <RetriesPanel groups={data?.retry_groups} loading={loading} />
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'danger';
}) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p
        className={`mt-1 text-2xl font-bold tabular-nums ${
          tone === 'danger' ? 'text-danger-700' : 'text-slate-900'
        }`}
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}
